import React, { useState, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Alert,
  CircularProgress,
  TextField,
  InputAdornment,
  Checkbox,
  Pagination,
} from '@mui/material';
import {
  Search as SearchIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import type { Category } from '../types';
import { resourceTopicService } from '../services/resourceTopicService';
import { useAppStore } from '../stores/useAppStore';
import { canManageCategories, isAdmin, isTeacher } from '../utils/rbac';
import { logger } from '../utils/logger';
import { useSnackbar } from '../hooks/useSnackbar';
import { ToastNotification } from './ToastNotification';

interface TopicListProps {
  onEdit: (topic: Category) => void;
  onAdd: () => void;
  refreshTrigger?: number;
}

export const TopicList: React.FC<TopicListProps> = ({ onEdit, onAdd, refreshTrigger }) => {
  const { user } = useAppStore();
  const userRoles = user?.roles || [];
  const canDelete = canManageCategories(userRoles);
  const isAdminRole = isAdmin(userRoles);
  const isTeacherRole = isTeacher(userRoles);
  // Teachers can create topics but only Admin/Approver/Librarian can delete
  const canCreate = canManageCategories(userRoles) || isTeacherRole;
  
  const [topics, setTopics] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [topicToDelete, setTopicToDelete] = useState<Category | null>(null);
  const [selectedTopicIds, setSelectedTopicIds] = useState<string[]>([]);
  const [bulkDeleteDialogOpen, setBulkDeleteDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const { snackbar, showSuccess, showError, hideSnackbar } = useSnackbar();

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Load topics
  useEffect(() => {
    loadTopics();
  }, [debouncedSearchTerm, refreshTrigger, page]);

  // Reset page when search term changes
  useEffect(() => {
    setPage(1);
  }, [debouncedSearchTerm]);

  const loadTopics = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const result = await resourceTopicService.getTopics({
        page,
        pageSize
      });
      
      let filtered = result.categories;
      
      // Apply client-side search filter if needed
      if (debouncedSearchTerm.trim()) {
        const normalized = debouncedSearchTerm.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
        filtered = result.categories.filter(topic => {
          const topicName = topic.name.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          const topicDesc = (topic.description || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
          return topicName.includes(normalized) || topicDesc.includes(normalized);
        });
      }
      
      setTopics(filtered);
      setTotalCount(result.totalCount);
      setTotalPages(result.totalPages);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load topics');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = (topic: Category) => {
    setTopicToDelete(topic);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!topicToDelete) return;

    try {
      await resourceTopicService.deleteTopic(topicToDelete.id);
      showSuccess('Xóa chủ đề thành công!');
      setDeleteDialogOpen(false);
      setTopicToDelete(null);
      loadTopics();
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || err?.error || 'Có lỗi xảy ra khi xóa chủ đề';
      showError(errorMessage);
    }
  };

  const handleBulkDelete = async () => {
    try {
      await Promise.all(selectedTopicIds.map(id => resourceTopicService.deleteTopic(id)));
      showSuccess(`Đã xóa ${selectedTopicIds.length} chủ đề thành công!`);
      setSelectedTopicIds([]);
      setBulkDeleteDialogOpen(false);
      loadTopics();
    } catch (err: any) {
      const errorMessage = err?.message || err?.error?.message || err?.error || 'Có lỗi xảy ra khi xóa chủ đề';
      showError(errorMessage);
    }
  };

  const handleSelectAll = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.checked) {
      setSelectedTopicIds(topics.map(topic => topic.id));
    } else {
      setSelectedTopicIds([]);
    }
  };

  const handleSelectTopic = (topicId: string) => {
    setSelectedTopicIds(prev => 
      prev.includes(topicId) 
        ? prev.filter(id => id !== topicId)
        : [...prev, topicId]
    );
  };

  const handlePageChange = (event: React.ChangeEvent<unknown>, value: number) => {
    setPage(value);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h5">Quản lý chủ đề sách số</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Tooltip title="Làm mới" arrow>
            <IconButton
              color="primary"
              onClick={loadTopics}
              aria-label="Làm mới"
              size="small"
            >
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Tooltip>
          {canCreate && (
            <Tooltip title="Thêm chủ đề" arrow>
              <IconButton
                color="primary"
                onClick={onAdd}
                aria-label="Thêm chủ đề"
                size="small"
              >
                <AddIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          {selectedTopicIds.length > 0 && canDelete && (
            <Tooltip title={`Xóa (${selectedTopicIds.length})`} arrow>
              <IconButton
                color="error"
                onClick={() => setBulkDeleteDialogOpen(true)}
                aria-label="Xóa"
                size="small"
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      <TextField
        fullWidth
        size="small"
        placeholder="Tìm kiếm chủ đề..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        sx={{ mb: 2 }}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ width: '20%' }}>Chủ đề</TableCell>
                  <TableCell sx={{ width: '35%' }}>Mô tả</TableCell>
                  <TableCell sx={{ width: '10%' }}>Ngày tạo</TableCell>
                  {(canDelete || canCreate) && <TableCell sx={{ width: '12%' }}>Thao tác</TableCell>}
                  {canDelete && (
                    <TableCell padding="checkbox" sx={{ width: '14px', textAlign: 'center', padding: '0px !important' }}>
                      <Checkbox
                        indeterminate={selectedTopicIds.length > 0 && selectedTopicIds.length < topics.length}
                        checked={topics.length > 0 && selectedTopicIds.length === topics.length}
                        onChange={handleSelectAll}
                        size="small"
                        sx={{ padding: '4px' }}
                      />
                    </TableCell>
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {topics.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={canDelete ? 4 : 3} align="center">
                      Không có chủ đề nào
                    </TableCell>
                  </TableRow>
                ) : (
                  topics.map((topic) => (
                    <TableRow key={topic.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="medium">
                          {topic.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" color="text.secondary">
                          {topic.description || 'Chưa có mô tả'}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ width: '10%' }}>
                        <Typography variant="body2">
                          {topic.dateCreated ? new Date(topic.dateCreated).toLocaleDateString('vi-VN') : 'N/A'}
                        </Typography>
                      </TableCell>
                      {(canDelete || canCreate) && (
                        <TableCell sx={{ width: '12%' }}>
                          {canCreate && (
                            <IconButton
                              size="small"
                              onClick={() => onEdit(topic)}
                              color="primary"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          )}
                          {canDelete && (
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteClick(topic)}
                              color="error"
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </TableCell>
                      )}
                      {canDelete && (
                        <TableCell padding="checkbox" sx={{ width: '14px', textAlign: 'center', padding: '0px !important' }}>
                          <Checkbox
                            checked={selectedTopicIds.includes(topic.id)}
                            onChange={() => handleSelectTopic(topic.id)}
                            size="small"
                            sx={{ padding: '4px' }}
                          />
                        </TableCell>
                      )}
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          {totalPages > 1 && (
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
              <Pagination
                count={totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn xóa chủ đề "{topicToDelete?.name}"? Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Hủy</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Delete Confirmation Dialog */}
      <Dialog open={bulkDeleteDialogOpen} onClose={() => setBulkDeleteDialogOpen(false)}>
        <DialogTitle>Xác nhận xóa</DialogTitle>
        <DialogContent>
          <Typography>
            Bạn có chắc chắn muốn xóa {selectedTopicIds.length} chủ đề đã chọn? Hành động này không thể hoàn tác.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkDeleteDialogOpen(false)}>Hủy</Button>
          <Button onClick={handleBulkDelete} color="error" variant="contained">
            Xóa
          </Button>
        </DialogActions>
      </Dialog>

      <ToastNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={hideSnackbar}
      />
    </Box>
  );
};

